import { useMemo } from "react";

import { BANNER_CALLOUT_TINT_ALPHA } from "../../models/banners";
import { useGetBoMeQuery } from "../../store/services/api";
import { getTenantPrimaryColor } from "../../utils/functions/tenantThemeColor";
import { sanitizeBannerRichText } from "./bannerRichText";

/**
 * BN-010 — Vista previa en vivo de la pantalla `BannerLandingPage`.
 *
 * Reproduce los 5 bloques de la spec §2 en el orden fijo (hero, título, callout, cuerpo, botón
 * sticky) para que Operaciones vea el resultado sin publicar. No es el render de la app: es la
 * misma jerarquía y los mismos tokens de tamaño/espaciado, en un marco de ancho mobile.
 */
interface BannerLandingPreviewProps {
  tenantId: string;
  heroImageUrl: string;
  title: string;
  /** HTML del cuerpo. Se vuelve a sanear acá: ver `bannerRichText`. */
  body: string;
  callout: string;
  buttonEnabled: boolean;
  buttonText: string;
  /** Titulo del bloque. En el formulario es una previa; en el detalle es el contenido real. */
  caption?: string;
}

const getBannerAccentColors = (primaryColor: string) => {
  const channels = [1, 3, 5].map((index) => parseInt(primaryColor.slice(index, index + 2), 16));
  const [red, green, blue] = channels.map((channel) => {
    const srgb = channel / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  const whiteContrast = 1.05 / (luminance + 0.05);
  const blackContrast = (luminance + 0.05) / 0.05;

  return {
    calloutBackground: `rgba(${channels.join(", ")}, ${BANNER_CALLOUT_TINT_ALPHA})`,
    buttonTextColor: whiteContrast >= blackContrast ? "#FFFFFF" : "#000000",
  };
};

export const BannerLandingPreview = ({
  tenantId,
  heroImageUrl,
  title,
  body,
  callout,
  buttonEnabled,
  buttonText,
  caption = "Vista previa",
}: BannerLandingPreviewProps) => {
  const { data: me } = useGetBoMeQuery();
  const tenantTheme = me?.tenants.find((tenant) => tenant.id === tenantId)?.theme;
  const primaryColor = useMemo(() => getTenantPrimaryColor(tenantTheme), [tenantTheme]);
  const bodyHtml = useMemo(() => sanitizeBannerRichText(body), [body]);
  const { calloutBackground, buttonTextColor } = getBannerAccentColors(primaryColor);

  return (
    <section className="banner-landing-preview" aria-label={caption}>
      <h2 className="banner-landing-preview__caption">{caption}</h2>

      <div className="banner-landing-preview__device">
        <div className="banner-landing-preview__scroll">
          {heroImageUrl ? (
            <img
              className="banner-landing-preview__hero"
              src={heroImageUrl}
              alt={title || "Imagen principal de la página interna"}
            />
          ) : (
            <div className="banner-landing-preview__hero banner-landing-preview__hero--empty">
              <i className="pi pi-image" aria-hidden />
            </div>
          )}

          <div className="banner-landing-preview__content">
            <h3 className="banner-landing-preview__title">
              {title || "Título de la página interna"}
            </h3>

            {/* §2.5: sin texto, el bloque no se renderiza — no deja el hueco vacío. */}
            {callout.trim() !== "" && (
              <div
                className="banner-landing-preview__callout"
                style={{
                  background: calloutBackground,
                  borderLeftColor: primaryColor,
                }}
              >
                {callout}
              </div>
            )}

            {bodyHtml ? (
              <div
                className="banner-landing-preview__body"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            ) : (
              <p className="banner-landing-preview__body banner-landing-preview__body--empty">
                El cuerpo de la página se ve acá.
              </p>
            )}
          </div>
        </div>

        {/* §2.7: footer sticky fuera del scroll, con sombra superior. Apagado el toggle, el área
            de lectura ocupa el 100% inferior y el botón directamente no existe. */}
        {buttonEnabled && (
          <div className="banner-landing-preview__footer">
            <span
              className="banner-landing-preview__button"
              style={{ background: primaryColor, color: buttonTextColor }}
            >
              {buttonText.trim() || "Texto del botón"}
            </span>
          </div>
        )}
      </div>

    </section>
  );
};
